import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarPlus, Route, ShieldCheck, Users } from "lucide-react";
import { api } from "@/lib/api";
import { EVENT_STATUS, fmtDate, fmtDur } from "@/lib/format";
import { Pill } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { PageTitle, Panel, Stat } from "./AdminLayout";
import { BarBox, LineBox, PieBox } from "./Charts";

function EventList({ title, items, testid }) {
  return (
    <Panel title={title}>
      <div className="space-y-2" data-testid={testid}>
        {items.length === 0 && <p className="text-sm text-muted-foreground">Tidak ada.</p>}
        {items.map((e) => (
          <Link key={e.id} to={`/admin/events/${e.id}`} className="flex items-center justify-between rounded-xl bg-[#F5F3EB] p-3 transition-colors hover:bg-[#EDE9DC]">
            <div><div className="font-semibold text-[#1A3323]">{e.name}</div><div className="text-xs text-muted-foreground">{fmtDate(e.date)} · {e.checkin_count} check-in</div></div>
            <Pill className={EVENT_STATUS[e.status].cls}>{EVENT_STATUS[e.status].label}</Pill>
          </Link>
        ))}
      </div>
    </Panel>
  );
}

export default function Overview() {
  const [o, setO] = useState(null);
  const [a, setA] = useState(null);
  useEffect(() => {
    api.get("/admin/overview").then((r) => setO(r.data));
    api.get("/admin/analytics").then((r) => setA(r.data));
  }, []);
  if (!o) return <Skeleton className="h-64 rounded-2xl" />;
  return (
    <div>
      <PageTitle title="Ringkasan" desc={`Performa hari ini · ${fmtDate(o.today)}`}>
        <div className="flex flex-wrap gap-2">
          <Link to="/admin/events?new=1"><Button className="rounded-full" data-testid="quick-create-event"><CalendarPlus className="mr-1.5 h-4 w-4" /> Buat Event</Button></Link>
          <Link to="/admin/routes"><Button variant="outline" className="rounded-full" data-testid="quick-manage-routes"><Route className="mr-1.5 h-4 w-4" /> Kelola Jalur</Button></Link>
          <Link to="/admin/lead"><Button variant="outline" className="rounded-full" data-testid="quick-verify-lead"><ShieldCheck className="mr-1.5 h-4 w-4" /> Verifikasi Lead</Button></Link>
          <Link to="/admin/participants"><Button variant="outline" className="rounded-full" data-testid="quick-participants"><Users className="mr-1.5 h-4 w-4" /> Peserta</Button></Link>
        </div>
      </PageTitle>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        <Stat accent label="Peserta hari ini" value={o.today_participants} testid="ov-today-participants" />
        <Stat label="Check-in" value={o.today_checkins} testid="ov-today-checkins" />
        <Stat label="Climb selesai" value={o.today_completed} testid="ov-today-completed" />
        <Stat label="Antrean aktif" value={o.active_queue} sub={`${o.climbing_now} sedang manjat`} testid="ov-active-queue" />
        <Stat label="Rata-rata rotasi" value={fmtDur(o.avg_duration_seconds)} testid="ov-avg-duration" />
        <Stat label="Overdue" value={`${o.overdue_pct}%`} sub="> standar 5 mnt" testid="ov-overdue-pct" />
      </div>
      <div className="mt-6 grid gap-4 lg:grid-cols-3">
        <Panel title="Tren kehadiran" className="lg:col-span-2"><LineBox data={a?.attendance_by_month} /></Panel>
        <Panel title="Gender"><PieBox data={a?.gender} /></Panel>
        <Panel title="Usia"><BarBox data={a?.age} /></Panel>
        <Panel title="Kota"><BarBox data={a?.city?.slice(0, 6)} horizontal color="#3D6F4B" /></Panel>
        <Panel title="Rata-rata durasi per jalur (detik)"><BarBox data={a?.duration_by_route} dataKey="avg_duration_seconds" color="#5C7F55" fmt={(v) => [`${v} detik`, "Rata-rata"]} /></Panel>
      </div>
      <div className="mt-6 grid gap-4 lg:grid-cols-3">
        <EventList title="Aktif" items={o.events.active} testid="ov-events-active" />
        <EventList title="Mendatang" items={o.events.upcoming} testid="ov-events-upcoming" />
        <EventList title="Selesai" items={o.events.completed} testid="ov-events-completed" />
      </div>
    </div>
  );
}
